<template>
  <div class="mx-auto">
    <chat-notification :title="notificationTitle">
      <div class="flex max-w-full flex-wrap items-center justify-center gap-x-1 text-center">
        <span
          v-if="executorDisplayName"
          class="font-medium text-fg-base-fg3"
          data-test="chat-pinned-event-executor"
        >
          {{ executorDisplayName }}
        </span>
        <span class="truncate">
          {{ messageText }}
          <span
            v-if="pinnedMessageText"
            class="cursor-pointer"
            @click="emit('scrollToItem', props.event.item.targetEventId)"
          >«<span v-html="pinnedMessageText" />...»</span>
        </span>
      </div>
    </chat-notification>
    <div
      v-if="isUnread"
      class="relative"
    >
      <span class="absolute right-[-10px] bottom-0 h-2 w-2 rounded-full bg-primary-tints-400" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { HistoryMessageItem, MessageIncomingEvent, MessageOutgoingEvent, MessagePinnedByOtherEvent, MessagePinnedByYouEvent, MessageUnPinnedByOtherEvent, MessageUnPinnedByYouEvent } from '@wui/im'
import { computed } from 'vue'

import { useStoreContacts } from '@/entities/contact'
import { getPinnedEventText, chatModel, useSafeUnreadState } from '@/entities/messages'
import { useStoreRooms } from '@/entities/room'

import { ChatNotification } from '@/shared/components/chat-notification'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'

const props = defineProps<{
  event: HistoryMessageItem<MessagePinnedByOtherEvent | MessagePinnedByYouEvent | MessageUnPinnedByOtherEvent | MessageUnPinnedByYouEvent>
}>()

const emit = defineEmits<{
  (event: 'scrollToItem', value: number): void
}>()

const { findById } = chatModel.useChatMessageStore()

const pinnedMessageText = computed(() => {
  const item = findById<MessageOutgoingEvent | MessageIncomingEvent>(props.event.item.targetEventId)?.item
  const text = item?.messageText || item?.forwarding?.forwardingText || ''
  return text.substring(0, 30)
})

const messageText = computed(() => {
  return getPinnedEventText(props.event)
})

const { currentUser } = useCurrentUser()
const { t } = useLocalization()
const { findGuestByPserved } = useStoreRooms()

const contact = computed(() => {
  if (currentUser.value?.imLogin === props.event.item.executor) {
    return currentUser.value
  }
  return useStoreContacts().findByPServed(props.event.item.executor)
})

const isGuestExecutor = computed(() => Boolean(props.event.item.executor?.includes('<sip:GUEST')))

const guestDisplayName = computed(() => {
  if (!isGuestExecutor.value) {
    return undefined
  }
  const name = findGuestByPserved(props.event.item.pServed, props.event.item.executor)
  return name ? `${name} (${t('common.guest')})` : undefined
})

const executorDisplayName = computed(() =>
  contact.value?.name
    ?? guestDisplayName.value
    ?? (isGuestExecutor.value ? t('common.guest') : ''),
)

const notificationTitle = computed(() => {
  const rest = `${messageText.value} ${pinnedMessageText.value}`.trim()
  return executorDisplayName.value ? `${executorDisplayName.value} ${rest}` : rest
})

const { isUnread } = useSafeUnreadState(props.event)
</script>
